// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

/**
 * Convert API errors into demo-friendly messages.
 *
 * Backend returns one of:
 *   - 4xx with { error: "code", message: "human-readable" }
 *   - 4xx/5xx with no body (network blip, gateway timeout)
 *   - thrown Error with .message
 *
 * This utility prefers the human message when present, falls back to a
 * code-aware default, and never shows raw HTTP status numbers to the user.
 */

import { ApiError } from './client'

const ERROR_CODE_MESSAGES: Record<string, string | null> = {
  // From the auth + workflow layer
  not_authenticated:     'You need to sign in first.',
  not_authorized:        'You don\u2019t have permission for this action.',
  wrong_owner:           'This prescription belongs to a different patient or provider.',
  not_found:             'That record could not be found. It may have been removed.',
  empty_body:            'The form was submitted without data \u2014 please fill it in and try again.',
  bad_json:              'The request couldn\u2019t be processed. Try refreshing the page.',
  bad_quantity:          'Quantity must be a positive number.',
  bad_machine:           'Internal mismatch \u2014 please refresh and try again.',
  // null entries: use the message verbatim (the backend's message is the right thing to show)
  validation:            null,
  invalid_transition:    null,
  reason_required:       null,
  wrong_state:           null,
  over_authorized:       null,
  already_decided:       'This refill request has already been decided.',
  rx_not_found:          'That prescription could not be found.',
  patient_or_prescriber_not_found: 'The patient or prescriber on the form could not be matched.',
  production_safeguard:  'This action is not available in this environment.',

  // ── D-CRX-29 T7 (Andrew's walk, 2026-07-17): "raw API strings never render to users."
  // He hit a backend sentence rendered verbatim into a clinical screen ("patient has multiple active
  // therapies; specify therapy_id"). Sasha at the round table: a raw server string is also an
  // information-disclosure path, not just bad UX. Every CareRx code gets wording written for a
  // pharmacist — none of these may be `null` (null = verbatim passthrough, see the debt note below).
  enrollment_incomplete: 'This enrollment cannot be approved yet.',
  therapy_id_required:   'This patient has more than one active therapy \u2014 open the specific therapy you mean. Each therapy has its own review.',
  required_sections_incomplete: 'The POT form cannot be completed yet \u2014 some required sections are incomplete.',
  required_missing:      'The POT form cannot be completed yet — some required fields are empty.',
  signature_required:    'A nurse attestation signature is required to complete this form.',
  pot_form_incomplete:   'The clinical POT form must be completed and attested before this visit can close.',
  claimed_by_another:    'Another nurse already claimed this visit.',
  dispense_only_therapy: 'This is a dispense-only therapy \u2014 it has no nurse visit or Plan of Treatment.',
  agency_not_found_or_inactive: 'That organization is not active. Pick another, or add it under Billing \u2192 Agencies.',
  nurse_not_found_or_inactive:  'That nurse account is not active.',
  visit_not_found_or_not_open:  'This visit is no longer open \u2014 it may have been completed or cancelled. Refresh to see the current state.',
  duration_hrs_required: 'Enter the visit hours \u2014 they drive the invoice.',
  visit_closed:          'This visit is closed. Completing it again would create a new version of the form.',
  no_draft:              'There is nothing to complete yet \u2014 save the form first.',
  draft_moved:           'This form changed while you were working. Reload before completing it.',
  pcw_has_no_therapy:    'This worksheet predates the enrollment door and has no therapy attached. Start a new enrollment from the patient profile.',
  therapy_not_found_or_terminal: 'That therapy is closed or no longer exists.',
  docusign_not_configured: 'E-signature sending is not switched on yet \u2014 the document is generated and saved.',
  visit_date_required:   'Pick the visit date.',
  bad_visit_type:        'That visit type is not one this lane supports.',
  pot_schema_missing:    'The POT form template is missing for this pharmacy. Tell the OwlRx team \u2014 this is a configuration issue, not your data.',
  self_approval_blocked: 'You cannot approve an invoice for your own visits \u2014 it needs another approver.',
  credential_expired:    'A required credential has expired \u2014 renew it before claiming visits.',
  rate_card_loa_unsigned:'This rate card has no signed LOA \u2014 a rate card is inactive until its LOA is signed.',
}

// DEBT (T7b, owner: build): the `null` entries above mean "render the backend message verbatim".
// That predates Andrew's rule and is the mechanism that leaked a raw string into a clinical screen.
// Flipping them globally requires reading every backend message they cover, so it is a reviewed
// follow-up, not a drive-by change. New codes must never be added as `null`.

const NETWORK_HINTS: { match: RegExp; message: string }[] = [
  { match: /Failed to fetch|NetworkError|ENOTFOUND|ECONNREFUSED/i,
    message: 'Couldn\u2019t reach the server. Check your connection and retry.' },
  { match: /CORS/i,
    message: 'A browser security check blocked the request. Please refresh and try again.' },
  { match: /timeout|timed out/i,
    message: 'The server took too long to respond. Retry in a moment.' },
]

export function friendlyError(e: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (!e) return fallback

  // Our own ApiError subclass with a body
  if (e instanceof ApiError) {
    const body = e.body as { error?: string; message?: string } | null

    // Code lookup first — but if message is rich, prefer it
    if (body?.error) {
      const code = body.error
      const codeMessage = ERROR_CODE_MESSAGES[code]
      if (codeMessage) return codeMessage
      // For null entries (validation, invalid_transition, etc.), use the message as-is
      if (codeMessage === null && body.message) return body.message
    }

    if (body?.message) return body.message

    // HTTP status fallbacks
    if (e.status === 401) return 'You need to sign in first.'
    if (e.status === 403) return 'You don\u2019t have permission for this action.'
    if (e.status === 404) return 'That record could not be found.'
    if (e.status >= 500)  return 'The server hit an unexpected problem. Please try again in a moment.'
    return fallback
  }

  // Plain Error or thrown thing with a .message
  const msg = (e as { message?: string })?.message
  if (msg) {
    for (const h of NETWORK_HINTS) {
      if (h.match.test(msg)) return h.message
    }
    return msg
  }

  return fallback
}

/**
 * Rich, debuggable error string — surfaces the FULL failure (no silent errors).
 * Always includes the operation, HTTP status, backend error code, and backend
 * message when present, e.g. "Load documents · HTTP 401 · anonymous · Authentication required."
 * Use this on every user-triggered action so a failure is never swallowed.
 */
export function debugError(op: string, e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { error?: string; message?: string } | null
    const parts: string[] = [op, `HTTP ${e.status}`]
    if (body?.error) parts.push(body.error)
    if (body?.message && body.message !== body?.error) parts.push(body.message)
    return parts.join(' · ')
  }
  const msg = (e as { message?: string })?.message
  if (msg) {
    for (const h of NETWORK_HINTS) if (h.match.test(msg)) return `${op} · ${h.message}`
    return `${op} · ${msg}`
  }
  return `${op} · unknown error (no response received)`
}

/**
 * D-CRX-29 T5/T7 — the rejection contract, rendered.
 *
 * The server names what is missing (422 + `missing[]`); this returns a title written for a
 * pharmacist plus that list, so a screen can show "cannot approve:" with the actual blockers instead
 * of a raw sentence or, worse, "the server hit an unexpected problem".
 *
 * `items` are server-authored, PHI-free strings created for display by the gate itself — the one
 * category of server text we deliberately surface. The backend's free-form `message` is not.
 */
export type MappedError = { title: string; items: string[] }

export function mapApiError(e: unknown, fallback?: string): MappedError {
  const body = (e instanceof ApiError ? (e.body as {
    error?: string; missing?: string[]; sections?: number[]; nurse?: string
  } | null) : null)
  const title = friendlyError(e, fallback)
  const items: string[] = []
  if (Array.isArray(body?.missing)) items.push(...body!.missing!)
  if (Array.isArray(body?.sections)) items.push(...body!.sections!.map(n => `Section ${n}`))
  if (body?.nurse) items.push(`Claimed by ${body.nurse}`)
  return { title, items }
}
